/**
 * StorefrontFieldEditModal — group edit modal for ALL storefront fields.
 *
 * Shows toggles (Publish, Catalog Only) + text fields (slug, description)
 * in a single modal. Toggles save immediately (optimistic), text fields
 * save on form submit — matching inline card behavior.
 *
 * NOT using MetaFieldEditModal because it cannot render:
 *   - URL preview below the slug input
 *   - Character counter for description
 *   - Live slug normalization on keystroke
 *   - Inline-saving toggles within a form modal
 *
 * Saves via usePatchSaveDoc (same hook MetaFieldEditModal uses internally).
 * Visual style matches MetaFieldEditModal's rounded-md field containers.
 */

import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Check } from "lucide-react";
import { usePatchSaveDoc } from "@/lib/hooks/usePatchSaveDoc";
import { itemQueryKeys } from "@/features/inventory/api/itemQueryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { slugify, displaySlug } from "./storefrontUtils";

interface StorefrontFieldEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current toggle states */
  enabled: boolean;
  catalogOnly: boolean;
  chatCheckoutEnabled: boolean;
  showInFeed: boolean;
  feedShowBundleContents: boolean;
  isBundle: boolean;
  /** Toggle handlers — fire immediately (optimistic save) */
  onToggleEnabled: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleCatalogOnly: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleChatCheckout: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleShowInFeed: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleFeedShowBundle: (e: React.MouseEvent<HTMLButtonElement>) => void;
  togglePending: boolean;
  /** Text field values */
  currentSlug: string;
  currentDescription: string;
  storefrontBaseUrl: string;
  itemName: string;
  onFieldChange: (fieldname: string, value: any) => void;
}

export function StorefrontFieldEditModal({
  open,
  onOpenChange,
  enabled,
  catalogOnly,
  chatCheckoutEnabled,
  showInFeed,
  feedShowBundleContents,
  isBundle,
  onToggleEnabled,
  onToggleCatalogOnly,
  onToggleChatCheckout,
  onToggleShowInFeed,
  onToggleFeedShowBundle,
  togglePending,
  currentSlug,
  currentDescription,
  storefrontBaseUrl,
  itemName,
  onFieldChange,
}: StorefrontFieldEditModalProps) {
  const [slugValue, setSlugValue] = useState(displaySlug(currentSlug));
  const [descValue, setDescValue] = useState(currentDescription);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const detailKey = itemQueryKeys.detail(itemName);

  const saveMutation = usePatchSaveDoc({
    doctype: "Item",
    docname: itemName,
    detailKey,
  });

  // Reset local state only when modal opens
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setSlugValue(displaySlug(currentSlug));
      setDescValue(currentDescription);
      setSubmitError(null);
    }
    prevOpen.current = open;
  }, [open, currentSlug, currentDescription]);

  const handleSlugChange = (raw: string) => {
    setSlugValue(slugify(raw));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const slugChanged = slugValue !== currentSlug;
    const descChanged = descValue !== currentDescription;

    // No text changes — just close (toggles already saved inline)
    if (!slugChanged && !descChanged) {
      onOpenChange(false);
      return;
    }

    setSubmitError(null);

    try {
      const payload: Record<string, any> = {};
      if (slugChanged) payload.storefront_slug = slugValue;
      if (descChanged) payload.storefront_short_description = descValue;

      await saveMutation.mutateAsync(payload);

      if (slugChanged) onFieldChange("storefront_slug", slugValue);
      if (descChanged) onFieldChange("storefront_short_description", descValue);

      queryClient.invalidateQueries({ queryKey: detailKey });
      onOpenChange(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to save changes";
      setSubmitError(msg);
    }
  };

  const slugPreview = `${storefrontBaseUrl}/product/${displaySlug(slugValue) || "your-product-slug"}`;
  const descLength = descValue.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden max-w-[680px]">
        {/* Top fade gradient */}
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />

        {/* Checkmark button — top right */}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={saveMutation.isPending}
          className={cn(
            "absolute right-4 top-4 z-50 rounded-full bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] shadow-sm",
            "focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          size="icon"
          title="Save changes"
        >
          <Check className="w-5 h-5 text-white" />
          <span className="sr-only">Save</span>
        </Button>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
            <DialogHeader className="px-10 pt-20 pb-4 text-center">
              <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
                Storefront
              </DialogTitle>
              <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
                Update the information below
              </DialogDescription>
              {submitError && (
                <div className="mt-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
                  <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                </div>
              )}
            </DialogHeader>

            <div className="pb-8 px-10 pt-2">
              <div className="py-2 space-y-5">
                {/* Toggle fields — save immediately on click */}
                <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                  {/* Publish to Storefront */}
                  <div className="flex items-center justify-between min-h-[52px] px-5 py-4">
                    <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                      Publish to Storefront
                    </label>
                    <Switch
                      checked={enabled}
                      onCheckedChange={() => {}}
                      onClick={onToggleEnabled}
                      disabled={togglePending}
                      aria-label="Publish to storefront"
                    />
                  </div>

                  <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/40" />

                  {/* Catalog Only */}
                  <div
                    className={cn(
                      "flex items-center justify-between min-h-[52px] px-5 py-4 transition-opacity",
                      enabled ? "" : "opacity-50 pointer-events-none"
                    )}
                  >
                    <div>
                      <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                        Catalog Only
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Visible but not purchasable
                      </p>
                    </div>
                    <Switch
                      checked={catalogOnly}
                      onCheckedChange={() => {}}
                      onClick={onToggleCatalogOnly}
                      disabled={togglePending || !enabled}
                      aria-label="Catalog only mode"
                    />
                  </div>

                  <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/40" />

                  {/* Chat Inbox Checkout */}
                  <div className="flex items-center justify-between min-h-[52px] px-5 py-4">
                    <div>
                      <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                        Chat Inbox Checkout
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Show in Order Builder
                      </p>
                    </div>
                    <Switch
                      checked={chatCheckoutEnabled}
                      onCheckedChange={() => {}}
                      onClick={onToggleChatCheckout}
                      disabled={togglePending}
                      aria-label="Chat inbox checkout"
                    />
                  </div>

                  <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/40" />

                  {/* Show in Feed */}
                  <div
                    className={cn(
                      "flex items-center justify-between min-h-[52px] px-5 py-4 transition-opacity",
                      enabled ? "" : "opacity-50 pointer-events-none"
                    )}
                  >
                    <div>
                      <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                        Show in Feed
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Display in product feed page
                      </p>
                    </div>
                    <Switch
                      checked={showInFeed}
                      onCheckedChange={() => {}}
                      onClick={onToggleShowInFeed}
                      disabled={togglePending || !enabled}
                      aria-label="Show in feed"
                    />
                  </div>

                  {/* Show Bundle Contents — only for bundle products when feed is on */}
                  {isBundle && showInFeed && enabled && (
                    <>
                      <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/40" />
                      <div className="flex items-center justify-between min-h-[52px] px-5 py-4">
                        <div>
                          <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal">
                            Show Bundle Contents
                          </label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Show what's included on feed card
                          </p>
                        </div>
                        <Switch
                          checked={feedShowBundleContents}
                          onCheckedChange={() => {}}
                          onClick={onToggleFeedShowBundle}
                          disabled={togglePending}
                          aria-label="Show bundle contents in feed"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Slug field */}
                <div
                  className={cn(
                    "bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40 transition-opacity",
                    enabled ? "" : "opacity-50 pointer-events-none"
                  )}
                >
                  <div className="px-5 py-4 space-y-3">
                    <div className="flex items-center justify-between min-h-[52px]">
                      <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal flex-shrink-0">
                        URL Slug
                      </label>
                      <div className="flex-1 max-w-[60%] flex justify-end">
                        <Input
                          type="text"
                          value={slugValue}
                          onChange={(e) => handleSlugChange(e.target.value)}
                          onBlur={() => setSlugValue((v) => slugify(v))}
                          placeholder="my-product-name"
                          className="flex-1 text-right !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground truncate">
                      {slugPreview}
                    </p>
                  </div>
                </div>

                {/* Description field */}
                <div
                  className={cn(
                    "bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40 transition-opacity",
                    enabled ? "" : "opacity-50 pointer-events-none"
                  )}
                >
                  <div className="px-5 py-4 space-y-3">
                    <label className="text-[17px] text-gray-900 dark:text-gray-100 font-normal block">
                      Short Description
                    </label>
                    <Textarea
                      value={descValue}
                      onChange={(e) => setDescValue(e.target.value)}
                      rows={4}
                      placeholder="Briefly describe this product for your customers..."
                      className="w-full !bg-transparent border-0 p-0 text-[17px] text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:ring-0 resize-none placeholder:text-gray-400"
                    />
                    <p
                      className={cn(
                        "text-xs text-right",
                        descLength > 160 ? "text-amber-500" : "text-muted-foreground"
                      )}
                    >
                      {descLength} / 160
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom fade gradient */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
          </div>

          {/* Sticky Save button */}
          <div className="flex justify-center px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              type="submit"
              disabled={saveMutation.isPending}
              className="w-full text-[17px] font-semibold bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white px-7 py-3.5 h-auto rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
